import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LogoutOptions } from '@auth0/auth0-react'; // Import LogoutOptions

// Define the type for the Auth0 logout function
type LogoutFunction = (options?: LogoutOptions) => Promise<void> | void;

// Define the structure for sync status data (matching SyncStatusIndicator)
export interface SyncStatusState { // Added export
  status: 'pending' | 'running' | 'completed' | 'error' | 'idle';
  message: string;
  progress: number;
  details?: string[];
}

// PWA state tracking interface
export interface PWAState {
  isPWA: boolean;
  pwaLastVisible: number | null;
  pwaLastBackground: number | null;
  pwaSessionGaps: number[];
}

interface AuthState {
  accessToken: string | null;
  isApiAuthReady: boolean; // Flag to indicate token exists (interceptor handles setting it)
  userTimezone: string | null; // Add state for user's timezone
  userBillingName: string | null; // Add state for user's billing name from settings
  logoutAuth0: LogoutFunction | null; // State to hold the Auth0 logout function
  isSetupComplete: boolean | null; // Add state for setup check status: null = unchecked, false = incomplete, true = complete
  syncTaskId: string | null; // Add state for the active sync task ID
  syncTaskType: 'trades' | 'recommendations' | null; // Add task type to determine correct endpoint
  syncTaskStatus: SyncStatusState | null; // Store the latest known status
  pwaState: PWAState; // PWA state tracking
  
  // SECURITY: User transition tracking for cache security
  currentUserId: string | null; // Currently authenticated user ID
  previousUserId: string | null; // Previous user ID for transition handling
  userTransitionTimestamp: number | null; // When the last user transition occurred
  
  setAccessToken: (token: string | null) => void;
  setUserTimezone: (timezone: string | null) => void; // Add setter for timezone
  setUserBillingName: (billingName: string | null) => void; // Add setter for billing name
  setLogoutFunction: (logoutFn: LogoutFunction | null) => void; // Setter for the logout function
  setIsSetupComplete: (status: boolean | null) => void; // Update type to accept boolean OR null
  setSyncTaskId: (taskId: string | null, taskType?: 'trades' | 'recommendations' | null) => void; // Add task type parameter
  setSyncTaskStatus: (status: SyncStatusState | null) => void; // Action to update status
  setPWAState: (pwaState: Partial<PWAState>) => void; // PWA state setter
  updatePWAVisibility: (isVisible: boolean) => void; // PWA visibility update
  recordPWASessionGap: (gapDuration: number) => void; // PWA session gap tracking
  clearUserData: () => void; // Add method to clear user data on logout
  
  // SECURITY: User transition tracking methods
  setCurrentUserId: (userId: string | null) => void; // Set current user ID
  handleUserTransition: (newUserId: string | null) => void; // Handle user login/logout/switch
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      isApiAuthReady: false, // Initialize as false
      userTimezone: null, // Initialize timezone as null
      userBillingName: null, // Initialize billing name as null
      logoutAuth0: null, // Initialize logout function as null
      isSetupComplete: null, // Initialize setup status as null
      syncTaskId: null, // Initialize sync task ID as null
      syncTaskType: null, // Initialize task type as null
      syncTaskStatus: null, // Initialize status as null
      
      // SECURITY: Initialize user transition tracking
      currentUserId: null,
      previousUserId: null,
      userTransitionTimestamp: null,
      
      pwaState: { // Initialize PWA state
        isPWA: false,
        pwaLastVisible: null,
        pwaLastBackground: null,
        pwaSessionGaps: []
      },
      setAccessToken: (token) => {
        // 2. Update the store state. isApiAuthReady now just means a token exists.
        set({
          accessToken: token,
          isApiAuthReady: !!token // Set ready state based purely on token presence
        });
        console.log(`[AuthStore] setAccessToken executed. Token ${token ? 'present' : 'null'}. isApiAuthReady set to: ${!!token}`);
      },
  // 3. Implement the timezone setter
  setUserTimezone: (timezone) => {
    set({ userTimezone: timezone });
    console.log(`[AuthStore] setUserTimezone executed. Timezone set to: ${timezone}`);
  },
  // Implement the billing name setter
  setUserBillingName: (billingName) => {
    set({ userBillingName: billingName });
    console.log(`[AuthStore] setUserBillingName executed. Billing name set to: ${billingName}`);
  },
  // Implement the logout function setter
  setLogoutFunction: (logoutFn) => {
    set({ logoutAuth0: logoutFn });
    console.log(`[AuthStore] setLogoutFunction executed. Logout function ${logoutFn ? 'set' : 'cleared'}.`);
  },  // Implement the setup status setter
  setIsSetupComplete: (status: boolean | null) => { // Parameter 'status' is now boolean | null
    set({ isSetupComplete: status });
    console.log(`[AuthStore] setIsSetupComplete executed. Status set to: ${status}`);
  },// Implement the sync task ID setter with task type
  setSyncTaskId: (taskId, taskType = null) => {
    set({ syncTaskId: taskId, syncTaskType: taskType });
    console.log(`[AuthStore] setSyncTaskId executed. Task ID set to: ${taskId}, Task Type: ${taskType}`);
  },  // Implement the sync task status setter
  setSyncTaskStatus: (status) => {
    set({ syncTaskStatus: status });
    console.log(`[AuthStore] setSyncTaskStatus executed. Status set to:`, status);
  },
  // PWA state management methods
  setPWAState: (pwaState) => {
    set((state) => ({ 
      pwaState: { ...state.pwaState, ...pwaState }
    }));
    console.log(`[AuthStore] setPWAState executed. PWA state updated:`, pwaState);
  },
  updatePWAVisibility: (isVisible) => {
    const currentTime = Date.now();
    set((state) => ({
      pwaState: {
        ...state.pwaState,
        pwaLastVisible: isVisible ? currentTime : state.pwaState.pwaLastVisible,
        pwaLastBackground: !isVisible ? currentTime : state.pwaState.pwaLastBackground
      }
    }));
    console.log(`[AuthStore] updatePWAVisibility executed. Visible: ${isVisible}`);
  },
  recordPWASessionGap: (gapDuration) => {
    set((state) => ({
      pwaState: {
        ...state.pwaState,
        pwaSessionGaps: [...state.pwaState.pwaSessionGaps.slice(-9), gapDuration] // Keep last 10 gaps
      }
    }));
    console.log(`[AuthStore] recordPWASessionGap executed. Gap duration: ${gapDuration}ms`);
  },
  // Clear user data on logout
  clearUserData: () => {
    set({ 
      userTimezone: null, 
      userBillingName: null, 
      isSetupComplete: null,
      accessToken: null,
      isApiAuthReady: false,
      syncTaskId: null,
      syncTaskType: null,
      syncTaskStatus: null,
      pwaState: { // Reset PWA state
        isPWA: false,
        pwaLastVisible: null,
        pwaLastBackground: null,
        pwaSessionGaps: []
      },
      // SECURITY: Clear user transition tracking
      currentUserId: null,
      previousUserId: null,
      userTransitionTimestamp: null
    });
    console.log(`[AuthStore] clearUserData executed. User data cleared.`);
  },

  // SECURITY: User transition tracking methods
  setCurrentUserId: (userId: string | null) => {
    set((state) => ({ 
      previousUserId: state.currentUserId, // Move current to previous
      currentUserId: userId,
      userTransitionTimestamp: Date.now()
    }));
    console.log(`[AuthStore] User ID updated: ${userId?.substring(0, 20)}...`);
  },

  handleUserTransition: (newUserId: string | null) => {
    set((state) => {
      const previousUserId = state.currentUserId;
      const transitionType = !previousUserId && newUserId ? 'login' : 
                            previousUserId && !newUserId ? 'logout' :
                            previousUserId && newUserId && previousUserId !== newUserId ? 'switch' : 'update';
      
      console.log(`[AuthStore] User transition: ${transitionType}`, {
        from: previousUserId?.substring(0, 20) + '...',
        to: newUserId?.substring(0, 20) + '...'
      });

      return {
        previousUserId: previousUserId,
        currentUserId: newUserId,
        userTransitionTimestamp: Date.now()
      };
    });
  },


    }),    {
      name: 'auth-store', // Key for localStorage
      partialize: (state) => ({
        // Only persist user settings data, not tokens or functions
        userTimezone: state.userTimezone,
        userBillingName: state.userBillingName,
        isSetupComplete: state.isSetupComplete,
        pwaState: state.pwaState, // Persist PWA state for session gap detection
        // SECURITY: Persist user transition tracking (for audit/debugging)
        currentUserId: state.currentUserId,
        previousUserId: state.previousUserId,
        userTransitionTimestamp: state.userTransitionTimestamp,
      }),
    }
  )
);

// SECURITY: Helper function to get user transition info
export const getUserTransitionInfo = (): { 
  currentUserId: string | null; 
  previousUserId: string | null; 
  timestamp: number | null 
} => {
  const state = useAuthStore.getState();
  return {
    currentUserId: state.currentUserId,
    previousUserId: state.previousUserId,
    timestamp: state.userTransitionTimestamp
  };
};